import {
  Boxes,
  CreditCard,
  FileSpreadsheet,
  HandCoins,
  Megaphone,
  MessageSquare,
  Package,
  Settings,
  ShieldCheck,
  Star,
  Store,
  TicketPercent,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import type { SidebarSection } from "@/components/layout/SidebarShell";

/** Store roles allowed into each dashboard area (mirrors backend AREA_ROLES). */
const AREA_ROLES = {
  ANY: ["OWNER", "MANAGER", "LISTINGS", "ORDERS", "CHAT", "FINANCE"],
  LISTINGS: ["OWNER", "MANAGER", "LISTINGS"],
  ORDERS: ["OWNER", "MANAGER", "ORDERS"],
  CHAT: ["OWNER", "MANAGER", "CHAT", "ORDERS"],
  ADS: ["OWNER", "MANAGER", "FINANCE"],
  FINANCE: ["OWNER", "FINANCE"],
  STORE: ["OWNER", "MANAGER"],
  OWNER: ["OWNER"],
} as const;

const PATH_AREA: Record<string, keyof typeof AREA_ROLES> = {
  "/vendor": "ANY",
  "/vendor/listings": "LISTINGS",
  "/vendor/listings/bulk": "LISTINGS",
  "/vendor/orders": "ORDERS",
  "/vendor/reviews": "ORDERS",
  "/vendor/messages": "CHAT",
  "/vendor/offers": "CHAT",
  "/vendor/ads": "ADS",
  "/vendor/drops": "LISTINGS",
  "/vendor/coupons": "STORE",
  "/vendor/wallet": "FINANCE",
  "/vendor/wallet/withdraw": "FINANCE",
  "/vendor/subscription": "FINANCE",
  "/billing": "FINANCE",
  "/vendor/kyc": "OWNER",
  "/vendor/staff": "OWNER",
  "/vendor/settings": "STORE",
};

/** Vendor dashboard navigation. */
export const VENDOR_NAV: SidebarSection[] = [
  {
    items: [
      { to: "/vendor", label: "Overview", icon: Store, end: true },
      { to: "/vendor/listings", label: "Listings", icon: Boxes },
      { to: "/vendor/listings/bulk", label: "Bulk upload", icon: FileSpreadsheet },
      { to: "/vendor/orders", label: "Orders", icon: Package },
      { to: "/vendor/reviews", label: "Reviews & Q&A", icon: Star },
      { to: "/vendor/messages", label: "Chats", icon: MessageSquare },
      { to: "/vendor/offers", label: "Offers", icon: HandCoins },
    ],
  },
  {
    title: "Grow",
    items: [
      { to: "/vendor/ads", label: "Ads & boosts", icon: Megaphone },
      { to: "/vendor/drops", label: "Reality Drops", icon: Zap },
      { to: "/vendor/coupons", label: "Coupons", icon: TicketPercent },
    ],
  },
  {
    title: "Money",
    items: [
      { to: "/vendor/wallet", label: "Wallet", icon: Wallet },
      { to: "/billing", label: "Billing", icon: CreditCard },
    ],
  },
  {
    title: "Store",
    items: [
      { to: "/vendor/kyc", label: "Verification", icon: ShieldCheck },
      { to: "/vendor/staff", label: "Staff", icon: Users },
      { to: "/vendor/settings", label: "Settings", icon: Settings },
    ],
  },
];

/** Area for a path: exact match first, then the longest registered prefix (e.g. /vendor/ads/123 → /vendor/ads). */
function areaFor(path: string): keyof typeof AREA_ROLES {
  if (PATH_AREA[path]) return PATH_AREA[path];
  const prefix = Object.keys(PATH_AREA)
    .filter((p) => p !== "/vendor" && path.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? PATH_AREA[prefix]! : "ANY";
}

export function canAccess(role: string | undefined, path: string): boolean {
  const area = areaFor(path);
  return !!role && (AREA_ROLES[area] as readonly string[]).includes(role);
}

/** The nav, trimmed to what a staff member's role can open. */
export function navForRole(role: string | undefined): SidebarSection[] {
  return VENDOR_NAV.map((s) => ({ ...s, items: s.items.filter((i) => canAccess(role, i.to)) })).filter((s) => s.items.length > 0);
}
